import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import LanguageToggle from "./LanguageToggle";
import { SignOut, UserCircle, Sparkle, Warning } from "@phosphor-icons/react";

function MaintenancePill() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState({ maint: false, signups: false, signin: false });

  useEffect(() => {
    if (!user || (user.role !== "admin" && user.role !== "manager")) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const [m, su, si] = await Promise.all([
          api.get("/admin/maintenance-mode"),
          api.get("/admin/signups-gate"),
          api.get("/admin/signin-gate"),
        ]);
        if (cancelled) return;
        setState({
          maint: !!m.data?.enabled,
          signups: !!su.data?.disabled,
          signin: !!si.data?.disabled,
        });
      } catch (e) {
        // silently ignore — pill simply won't render
      }
    };
    load();
    const id = setInterval(load, 30000); // refresh every 30s
    return () => { cancelled = true; clearInterval(id); };
  }, [user]);

  if (!user || (user.role !== "admin" && user.role !== "manager")) return null;
  const { maint, signups, signin } = state;
  if (!maint && !signups && !signin) return null;

  const label = maint
    ? "Maintenance ON"
    : signups && signin
      ? "Sign-up + sign-in paused"
      : signups
        ? "Sign-ups paused"
        : "Sign-in paused";

  return (
    <button
      type="button"
      onClick={() => nav("/admin")}
      className="inline-flex items-center gap-1.5 rounded-full border border-[#CBA35C]/50 bg-[#CBA35C]/15 px-3 py-1 text-[11px] tracking-widest uppercase text-[#8C6B21] hover:bg-[#CBA35C]/25 transition"
      title="Click to manage in Admin Panel"
      data-testid="header-maintenance-pill"
    >
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full rounded-full bg-[#CBA35C] opacity-75 animate-ping" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-[#CBA35C]" />
      </span>
      <Warning size={12} weight="fill" />
      {label}
    </button>
  );
}

export default function Header({ variant = "light" }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const t = useT();
  return (
    <header className="backdrop-blur-xl bg-[#FBF9F6]/80 border-b border-[#E5E0D8] sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center group" data-testid="brand-link" aria-label="Zcardon home">
          <img
            src="/zcardon-logo.png"
            alt="Zcardon — Anyone Card"
            className="h-10 md:h-12 w-auto select-none group-hover:opacity-90 transition-opacity"
            draggable="false"
          />
        </Link>
        <nav className="hidden md:flex items-center gap-10 text-sm">
          <Link to="/" className="link-ul" data-testid="nav-home">{t("header.nav.home")}</Link>
          <Link to="/rates" className="link-ul" data-testid="nav-rates">{t("header.nav.rates")}</Link>
          <Link to="/support" className="link-ul" data-testid="nav-support">{t("header.nav.support")}</Link>
        </nav>
        <div className="flex items-center gap-3">
          <LanguageToggle />
          <MaintenancePill />
          {user ? (
            <>
              <Button variant="ghost" onClick={() => nav(user.role === "admin" ? "/admin" : user.role === "manager" ? "/manager" : user.role === "agent" ? "/agent" : "/dashboard")} data-testid="nav-dashboard">
                <UserCircle size={20} weight="duotone" className="mr-1" /> {user.full_name?.split(" ")[0] || t("header.nav.account")}
              </Button>
              <Button variant="outline" onClick={() => { logout(); nav("/"); }} data-testid="nav-logout">
                <SignOut size={18} className="mr-1" /> {t("header.nav.signout")}
              </Button>
            </>
          ) : (
            <>
              <Link to="/login?role=agent" className="text-xs tracking-widest uppercase text-[#4A5C50] link-ul hidden md:block" data-testid="nav-agent">Agent</Link>
              <Link to="/login?role=admin" className="text-xs tracking-widest uppercase text-[#4A5C50] link-ul hidden md:block" data-testid="nav-admin">{t("header.nav.admin")}</Link>
              <Link to="/login" className="text-sm link-ul" data-testid="nav-login">{t("header.nav.signin")}</Link>
              <Button className="bg-[#184A30] hover:bg-[#0F3320] rounded-xl text-white" onClick={() => nav("/register")} data-testid="nav-register">
                {t("header.nav.get_started")}
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
